import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { getOrderByIdAdmin } from '../../../api/redux/slices/Admin';
import { LoaderComponent } from '../../../helpers/components/loader';
import { toast } from 'react-toastify';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import ImprimerLogo from '../../../assets/icons/svg/document-text.svg';
import { ShopInfo, SuperAdminInfo } from '../../../components/userInfo';
import { useReactToPrint } from 'react-to-print';
import PrintToPdf from './PrintToPdf';
import { ShowTvaValues } from '../../../utils/groupTvaValues';

const OrderDetail = () => {
	const { orderDetails } = useSelector((state) => state.admin);
	const dispatch = useDispatch();
	const params = useParams();
	const navigate = useNavigate();
	const token = localStorage.getItem('accesstoken');

	const [detailOrder, setDetailOrder] = useState(orderDetails ?? {});
	const [loadingData, setLoadingData] = useState(false);
	const [countryMap, setCountryMap] = useState({});
	const [countryNames, setCountryNames] = useState({});

	const componentRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
	});

	// Using the SAME logic as PrintToPdf component (working)
	useEffect(() => {
		fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
			.then((res) => res.json())
			.then((data) => {
				const map = {};
				const names = {};
				data.forEach((c) => {
					if (c.cca3 && c.cca2) {
						map[c.cca3] = c.cca2.toLowerCase();
						names[c.cca3] = c.name.common;
					}
				});
				setCountryMap(map);
				setCountryNames(names);
			})
			.catch((err) => console.error('Failed to fetch country codes:', err));
	}, []);

	const getFlagUrl = (cca3) => {
		if (!cca3 || !countryMap[cca3]) return null;
		return `https://hatscripts.github.io/circle-flags/flags/${countryMap[cca3]}.svg`;
	};

	const getCountryName = (cca3) => {
		return countryNames[cca3] || cca3;
	};

	const stableDispatchOrderById = useCallback(() => {
		setLoadingData(true);
		dispatch(
			getOrderByIdAdmin({
				token: token,
				id: params.id,
			}),
		)
			.unwrap()
			.then((res) => {
				setDetailOrder(res.data);
			})
			.catch((err) => {
				toast.error(err?.error?.message || 'can not fetch this order', {
					position: 'top-center',
					autoClose: 2000,
				});
				setTimeout(() => {
					navigate(`/admin/orders/${params.page}`);
				}, 1000);
			})
			.finally(() => setLoadingData(false));
	}, [dispatch, params, token, navigate]);

	useEffect(() => {
		if (token && params.id) {
			stableDispatchOrderById();
		}
	}, [stableDispatchOrderById, token, params]);

	const getOriginalPrice = (el) => {
		if (el?.original_price) return el.original_price;
		const remise = el?.remise || 0;
		const priceAfterRemise = el?.prise_product_HT || 0;
		if (remise > 0) {
			return priceAfterRemise / (1 - remise / 100);
		}
		return priceAfterRemise;
	};

	const hasCompany = () => {
		return detailOrder?.order?.clientInfo?.companyClient ? true : false;
	};

	const isClientRegistered = () => {
		return detailOrder?.order?.clientInfo?.is_registered === true;
	};

	const calculatePlatformFee = () => {
		if (!hasCompany()) {
			return 0;
		}
		const total = detailOrder?.order?.total || 0;
		return total * 0.1;
	};

	const platformFee = calculatePlatformFee();

	// Check if shop is in Tunisia
	const isTunisia =
		!detailOrder?.order?.shopInfo?.country || detailOrder?.order?.shopInfo?.country === 'TUN';
	const shopCountry = detailOrder?.order?.shopInfo?.country;

	return (
		<div id='orderDetails'>
			<div className='details-order-header'>
				<Link to={`/admin/orders/${params.page}`}>
					<icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
					<h1>Bons de commandes</h1>
				</Link>
				<div style={{ display: 'flex', gap: '10px' }}>
					<Link to={`/admin/orders/order_update/${params.page}/${params.id}`}>
						<button className='button-nature'>Modifier</button>
					</Link>
					<button
						style={{ display: 'flex', gap: '10px' }}
						onClick={handlePrint}
						className='button-primary'
					>
						<img src={ImprimerLogo} alt='imprimer logo' />
						Imprimer
					</button>
				</div>
			</div>
			{loadingData ? (
				<LoaderComponent />
			) : (
				<>
					<div className='container'>
						<Fragment>
							{/* Alert for non-registered client */}
							{!isClientRegistered() && (
								<div
									className='platform-fee-alert'
									style={{
										backgroundColor: '#FFF8E1',
										border: '2px solid #FF9800',
										borderRadius: '8px',
										padding: '15px 20px',
										marginBottom: '20px',
										display: 'flex',
										alignItems: 'center',
										gap: '15px',
									}}
								>
									<div
										style={{
											backgroundColor: '#FF9800',
											borderRadius: '50%',
											width: '40px',
											height: '40px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
										}}
									>
										<icons.BsPersonPlus style={{ color: 'white', fontSize: '20px' }} />
									</div>
									<div>
										<h3
											style={{
												margin: '0 0 5px 0',
												color: '#E65100',
												fontSize: '16px',
												fontWeight: 'bold',
											}}
										>
											👤 Client non inscrit
										</h3>
										<p
											style={{ margin: '0', color: '#E65100', fontSize: '14px', lineHeight: '1.5' }}
										>
											Ce client a été ajouté manuellement par l'administrateur et n'a pas de compte
											sur la plateforme.
										</p>
									</div>
								</div>
							)}

							{detailOrder?.order?.status === 'ORDER' && hasCompany() && (
								<div
									className='platform-fee-alert'
									style={{
										backgroundColor: '#FFF3CD',
										border: '2px solid #FFA000',
										borderRadius: '8px',
										padding: '15px 20px',
										marginBottom: '20px',
										display: 'flex',
										alignItems: 'center',
										gap: '15px',
									}}
								>
									<div
										style={{
											backgroundColor: '#FFA000',
											borderRadius: '50%',
											width: '40px',
											height: '40px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
										}}
									>
										<icons.BsExclamationTriangle style={{ color: 'white', fontSize: '20px' }} />
									</div>
									<div>
										<h3
											style={{
												margin: '0 0 5px 0',
												color: '#856404',
												fontSize: '16px',
												fontWeight: 'bold',
											}}
										>
											📢 Frais de Plateforme Marketing
										</h3>
										<p
											style={{ margin: '0', color: '#856404', fontSize: '14px', lineHeight: '1.5' }}
										>
											<strong>10% du montant total</strong> de cette commande sera prélevé pour la
											plateforme afin de soutenir les objectifs marketing. <br />
											Montant total :{' '}
											<strong>{roundToThreeDecimals(detailOrder?.order?.total || 0)} DT</strong> →
											Frais de plateforme (10%) :{' '}
											<strong style={{ color: '#D32F2F', fontSize: '15px' }}>
												{roundToThreeDecimals(platformFee)} DT
											</strong>
										</p>
									</div>
								</div>
							)}

							{detailOrder?.order?.status === 'ORDER' && !hasCompany() && (
								<div
									className='platform-fee-alert'
									style={{
										backgroundColor: '#E8F5E9',
										border: '2px solid #4CAF50',
										borderRadius: '8px',
										padding: '15px 20px',
										marginBottom: '20px',
										display: 'flex',
										alignItems: 'center',
										gap: '15px',
									}}
								>
									<div
										style={{
											backgroundColor: '#4CAF50',
											borderRadius: '50%',
											width: '40px',
											height: '40px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
										}}
									>
										<icons.BsCheckCircle style={{ color: 'white', fontSize: '20px' }} />
									</div>
									<div>
										<h3
											style={{
												margin: '0 0 5px 0',
												color: '#2E7D32',
												fontSize: '16px',
												fontWeight: 'bold',
											}}
										>
											✅ Frais de Plateforme Marketing
										</h3>
										<p
											style={{ margin: '0', color: '#2E7D32', fontSize: '14px', lineHeight: '1.5' }}
										>
											Aucun frais de marketing n'est appliqué car le client n'est pas associé à une
											entreprise.
											<br />
											Frais de plateforme : <strong>0 DT</strong>
										</p>
									</div>
								</div>
							)}

							{!isTunisia && (
								<div
									className='platform-fee-alert'
									style={{
										backgroundColor: '#E0F2FE',
										border: '2px solid #0284C7',
										borderRadius: '8px',
										padding: '15px 20px',
										marginBottom: '20px',
										display: 'flex',
										alignItems: 'center',
										gap: '15px',
									}}
								>
									<div
										style={{
											backgroundColor: '#0284C7',
											borderRadius: '50%',
											width: '40px',
											height: '40px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
										}}
									>
										<icons.BsGlobe style={{ color: 'white', fontSize: '20px' }} />
									</div>
									<div>
										<h3
											style={{
												margin: '0 0 5px 0',
												color: '#075985',
												fontSize: '16px',
												fontWeight: 'bold',
											}}
										>
											🌍 Commande Internationale
										</h3>
										<p
											style={{ margin: '0', color: '#075985', fontSize: '14px', lineHeight: '1.5' }}
										>
											Cette commande est destinée à un shop hors Tunisie. TVA et timbre ne sont pas
											applicables.
										</p>
									</div>
								</div>
							)}

							<div className='credentials'>
								<div className='infos-societe left' style={{ border: 'none' }}>
									<label style={{ textTransform: 'uppercase' }}>🛒 JEUN'ESS MARKET</label>
								</div>
								<div className='left'>
									<div className='total'>
										<label>Montant Payer</label>
										<span>{roundToThreeDecimals(detailOrder?.order?.total_paid) || 0} DT</span>
									</div>
									<div className='total'>
										<label>Montant Restant</label>
										<span>
											{roundToThreeDecimals(detailOrder?.order?.remaining_amount) || 0} DT
										</span>
									</div>
								</div>
							</div>
							<div className='credentials'>
								<div className='left'>
									<div className='left-el'>
										<label>Numéro</label>
										<span>{detailOrder?.order?.reference ?? '-'}</span>
									</div>
									<div className='left-el'>
										<label>Date</label>
										<span>{convertDate(detailOrder?.order?.date, 'DD-MM-YYYY') ?? ''}</span>
									</div>
									<div className='left-el'>
										<label>Référence code client</label>
										<span>{detailOrder?.order?.clientInfo?.customs_code || '-'}</span>
									</div>
									{!isClientRegistered() && (
										<div className='left-el'>
											<label style={{ color: '#FF9800' }}>Statut client</label>
											<span style={{ color: '#FF9800', fontWeight: 'bold' }}>
												Non inscrit (ajout manuel)
											</span>
										</div>
									)}
								</div>
								<div className='left'>
									<div className='left-e'>
										<label style={{ textTransform: 'capitalize' }}>
											{detailOrder?.order?.clientInfo?.company_name ?? ''}
										</label>
									</div>
									<div className='left-e'>
										<span>{detailOrder?.order?.clientInfo?.client ?? ''}</span>
									</div>
									<div className='left-e'>
										<span>
											Matricule Fiscale:{' '}
											{detailOrder?.order?.clientInfo?.tax_registration_number
												? detailOrder.order.clientInfo.tax_registration_number
												: '-'}
										</span>
									</div>
									<div className='left-e'>
										<span>Client type : {detailOrder?.order?.clientInfo?.categories ?? ''}</span>
									</div>
									<div className='left-e'>
										<span>N° téléphone: {detailOrder?.order?.clientInfo?.phone ?? ''}</span>
									</div>
									<div className='left-e'>
										<ShopInfo shop={detailOrder?.order?.shopInfo} />
										{shopCountry && (
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
													marginTop: '4px',
												}}
											>
												{getFlagUrl(shopCountry) && (
													<img
														src={getFlagUrl(shopCountry)}
														alt={getCountryName(shopCountry)}
														width='20'
														height='20'
														style={{ borderRadius: '50%' }}
													/>
												)}
												<span style={{ fontSize: '0.85rem', color: '#35435f', fontWeight: 600 }}>
													{getCountryName(shopCountry)}
												</span>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className='devis'>
								<div className='devis-head'>
									<h1>Devis</h1>
									<p>
										Cher Client,
										<br />
										Nous avons bien reçu votre demande de devis et nous vous en remercions.
										<br />
										Nous vous prions de trouver ci-dessous nos conditions les meilleures
									</p>
								</div>
								<div className='devis-table'>
									<table className='offres'>
										<thead>
											<tr id='titresTable'>
												<th>RÉFÉRENCE</th>
												<th>RÉF.CLT</th>
												<th>DÉSIGNATION</th>
												<th>QTÉ</th>
												<th>VOLUME</th>
												<th>PU HT</th>
												<th>REMISE (%)</th>
												<th>PU A.R</th>
												<th>TVA (%)</th>
												<th>MONTANT HT</th>
											</tr>
										</thead>
										<tbody>
											{detailOrder?.list?.map((el, index) => {
												const originalPrice = getOriginalPrice(el);
												const priceAfterRemise = el?.prise_product_HT || 0;
												const totalHT = el?.montantHT || priceAfterRemise * (el?.quantity || 0);

												return (
													<tr className='elementsTable' key={index}>
														<td>{el?.reference ?? '-'}</td>
														<td>{el?.refranceCTL ?? '-'}</td>
														<td>{el?.designation ?? '-'}</td>
														<td>{el?.quantity ?? '-'}</td>
														<td>{el?.volume != null ? roundToThreeDecimals(el.volume) : '-'}</td>
														<td>{roundToThreeDecimals(originalPrice)} DT</td>
														<td>{el?.remise ?? 0}%</td>
														<td>{roundToThreeDecimals(priceAfterRemise)} DT</td>
														<td>{el?.tva ?? 0}%</td>
														<td>{roundToThreeDecimals(totalHT)} DT</td>
													</tr>
												);
											})}
										</tbody>
									</table>
									<div className='total-devis'>
										<div id='box'>
											<div>
												<h1>TOTAL HT</h1>
												<span id='somme-totale'>
													{roundToThreeDecimals(detailOrder?.order?.total_HT ?? 0)} DT
												</span>
											</div>
											{ShowTvaValues(detailOrder)}
											{isTunisia && (
												<div>
													<h1>TIMBRE</h1>
													<span id='somme-totale'>
														{roundToThreeDecimals(detailOrder?.order?.timbre ?? 0)} DT
													</span>
												</div>
											)}
											<div>
												<h1>TOTAL TTC</h1>
												<span id='somme-totale'>
													{roundToThreeDecimals(detailOrder?.order?.total ?? 0)} DT
												</span>
											</div>
											<div
												style={{
													borderTop: '2px solid #FFA000',
													paddingTop: '10px',
													marginTop: '10px',
												}}
											>
												<h1 style={{ color: hasCompany() ? '#D32F2F' : '#4CAF50' }}>
													FRAIS MARKETING (10%)
												</h1>
												<span
													id='somme-totale'
													style={{ color: hasCompany() ? '#D32F2F' : '#4CAF50' }}
												>
													{roundToThreeDecimals(platformFee)} DT
												</span>
											</div>
										</div>
									</div>
								</div>
								<div className='message'>
									<p>
										Nous sommes à votre disposition pour tout complément d'informations. <br />
										Nous vous prions d'agréer, Cher Client, nos sincères salutations. <br />
										Le service commercial.
									</p>
								</div>
								<SuperAdminInfo superAdminInfo={[detailOrder?.order?.companyInfo]} />
							</div>
						</Fragment>
					</div>
					<div style={{ display: 'none' }}>
						<PrintToPdf
							componentRef={componentRef}
							adminCompany={orderDetails?.adminCompany}
							orderDetails={detailOrder}
							companyInfos={detailOrder?.order?.companyInfo}
							isAdmin={true}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default OrderDetail;
